import { all, takeEvery, call, put, select } from 'redux-saga/effects';
import { defineMessages } from 'react-intl';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';

import { CLASS_ENTITY_TYPE, PERMISSION } from 'constants/logic';
import URLS from 'constants/urls';
import { trackClassStatusChange } from 'helpers/events';
import { IA_KEY } from 'helpers/ia-logic';
import { makePath } from 'helpers/link-generation';
import { selectActiveClassId, selectActiveVenue, selectIaLogic, selectUserHasPermission } from 'selectors/general';
import { classDetail, activityTagTree, venueClasses } from 'store/saga/entities/bound';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { confirmClassDisable } from 'components/DisableClassConfirmation';
import { waitForModal } from 'components/Modal';
import { doFetchVenueSmartspotData } from 'components/SmartSpot';
import { ACTIVE_VENUE_CHANGED } from 'features/App';
import { freeClassMessages } from './ClassDetail.messages';

export const messages = defineMessages({
  doUpdateClassDetailSuccess: {
    id: 'class-detail.class-detail-saga.do-update-class-detail-success',
    defaultMessage: 'You published new content to your Class Detail page',
  },
  doUpdateClassDetailFailure: {
    id: 'class-detail.class-detail-saga.do-update-class-detail-failure',
    defaultMessage: 'We were unable to update your content at this time',
  },
  classNotFound: {
    id: 'class-detail.class-detail-saga.class-not-found',
    defaultMessage: "We couldn't find that class. It may have been removed.",
  },
});

export const ENABLE_FREE_CLASS = 'ENABLE_FREE_CLASS';
export const enableFreeClass = (venueId, classId) => ({
  type: ENABLE_FREE_CLASS,
  venueId,
  classId,
});

export const UPDATE_CLASS_DETAIL = 'UPDATE_CLASS_DETAIL';
export const updateClassDetail = (values, formAsyncFns) => ({
  type: UPDATE_CLASS_DETAIL,
  values,
  formAsyncFns,
});

export const UPDATE_IS_CLASS_ENABLED = 'UPDATE_IS_CLASS_ENABLED';
export const updateIsClassEnabled = (venueClass, isEnabled, source) => ({
  type: UPDATE_IS_CLASS_ENABLED,
  venueClass,
  isEnabled,
  source,
});

export const UPDATE_CLASS_NETWORK_SPOTS = 'UPDATE_CLASS_NETWORK_SPOTS';
export const updateClassNetworkSpots = networkSpots => ({ type: UPDATE_CLASS_NETWORK_SPOTS, networkSpots });

function* fetchClassDetailOrRedirect(client, venueId, classId) {
  try {
    yield call(fetchEntityIfNeeded, classDetail, [client, venueId, classId]);
  } catch (error) {
    if (error?.response?.status !== 404) {
      throw error;
    }
    yield put(createErrorBanner(messages.classNotFound));
    yield call(client.navigate, makePath.editClass(venueId));
  }
}

export function* handleClassRouteEnter(client) {
  const { venueId } = yield select(selectActiveVenue);
  yield call(doFetchVenueSmartspotData, client, { venueId });
  const { featureIsSupported } = yield select(selectIaLogic, IA_KEY.MANAGE_CLASS_PAGES, venueId);
  const classId = yield select(selectActiveClassId);

  if (featureIsSupported) {
    yield all([
      call(fetchClassDetailOrRedirect, client, venueId, classId),
      call(fetchEntityIfNeeded, activityTagTree, [client]),
    ]);
  }
}

export function* doUpdateClassDetail(dependencies, action) {
  const { values, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;

  try {
    const { isMsInventoryVenue, venueId } = yield select(selectActiveVenue);
    const {
      activeTagIds,
      classId,
      classTitle: className,
      tagsById,
      activeLevel1TagId,
      activeLevel2TagId,
      activeLevel3TagIds = [],
      ...restValues
    } = values;

    // Filter out all tags that are not a subtag of the selected level 1 tag
    const newTagIds = [activeLevel1TagId, activeLevel2TagId, ...activeLevel3TagIds].filter(Boolean);
    const updatedValues = { newTagIds, ...restValues };

    if (isMsInventoryVenue) {
      updatedValues.className = className;
    }

    yield call(fetchEntity, classDetail, [dependencies, venueId, classId, updatedValues]);
    yield call(resolveSubmit, messages.doUpdateClassDetailSuccess);
  } catch (error) {
    yield all([put(createErrorBanner(messages.doUpdateClassDetailFailure)), call(rejectSubmit)]);
  }
}

export function* doUpdateIsClassEnabled(dependencies, action) {
  try {
    const { httpClient } = dependencies;
    const { isEnabled, venueClass, source } = action;
    const { classId } = venueClass;
    const { venueId } = yield select(selectActiveVenue);
    const updateClassEndpoint = URLS.class.update(classId);

    if (!isEnabled) {
      const confirmed = yield call(confirmClassDisable, dependencies, {
        venueId,
        venueClass,
        entityType: CLASS_ENTITY_TYPE.CLASS,
      });
      if (!confirmed) return;
    }

    const includeDisabledClasses = yield select(selectUserHasPermission, PERMISSION.FEATURE_DISABLE_INVENTORY);

    yield call([httpClient, httpClient.patch], updateClassEndpoint, { disabled: !isEnabled });
    yield call(trackClassStatusChange, { hasEnabled: isEnabled, source, classId });

    yield call(fetchEntity, classDetail, [dependencies, venueId, classId]);
    yield call(fetchEntity, venueClasses, [
      dependencies,
      { venueId, onlyActive: !includeDisabledClasses, includeDisabled: includeDisabledClasses },
    ]);
  } catch (error) {
    yield put(createErrorBanner(messages.doUpdateClassDetailFailure));
  }
}

export function* doUpdateClassNetworkSpots(dependencies, action) {
  try {
    const { httpClient } = dependencies;
    const { networkSpots } = action;
    const { venueId } = yield select(selectActiveVenue);
    const classId = yield select(selectActiveClassId);
    const updateClassEndpoint = URLS.class.update(classId);

    yield call([httpClient, httpClient.patch], updateClassEndpoint, { spots: networkSpots });
    yield call(fetchEntity, classDetail, [dependencies, venueId, classId]);
  } catch (error) {
    yield put(createErrorBanner(messages.doUpdateClassDetailFailure));
  }
}

export function* doEnableFreeClass(dependencies, { classId, venueId }) {
  const { httpClient } = dependencies;
  const updateClassEndpoint = URLS.class.update(classId);
  const confirmed = yield call(waitForModal, dependencies, 'EnableFreeClassModal');
  if (confirmed) {
    try {
      yield call([httpClient, httpClient.patch], updateClassEndpoint, {
        disabled: false,
        is_free: true,
      });
      yield call(fetchEntity, classDetail, [dependencies, venueId, classId]);
      yield put(createInfoBanner(freeClassMessages.setFreeSuccess));
    } catch (error) {
      yield put(createErrorBanner(freeClassMessages.setFreeError));
    }
  }
}

export function* handleActiveVenueChange(client, action) {
  const { venueId } = action;
  yield call(client.navigate, makePath.editClass(venueId));
}

export function* watchClassDetailPage(client) {
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, client);
  yield takeEvery(ENABLE_FREE_CLASS, doEnableFreeClass, client);
  yield takeEvery(UPDATE_CLASS_DETAIL, doUpdateClassDetail, client);
  yield takeEvery(UPDATE_IS_CLASS_ENABLED, doUpdateIsClassEnabled, client);
  yield takeEvery(UPDATE_CLASS_NETWORK_SPOTS, doUpdateClassNetworkSpots, client);
}
